import { useEffect, useState } from "react";
import { format, isBefore, startOfDay, endOfDay, startOfMonth, subDays } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";
import { Button } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CompareMode } from "@/lib/utils/comparison-dates";
import { getComparisonRange } from "@/lib/utils/comparison-dates";

interface AppointmentsPeriodHeroProps {
  dateFrom: Date;
  dateTo: Date;
  compareMode: CompareMode;
  totalAppointments: number;
  activeProfessionals: number;
  onDateFromChange: (d: Date) => void;
  onDateToChange: (d: Date) => void;
  onCompareModeChange: (m: CompareMode) => void;
}

export function AppointmentsPeriodHero({
  dateFrom,
  dateTo,
  compareMode,
  totalAppointments,
  activeProfessionals,
  onDateFromChange,
  onDateToChange,
  onCompareModeChange,
}: AppointmentsPeriodHeroProps) {
  const { t, i18n } = useTranslation("reports");
  const locale = i18n.language === "pt" ? ptBR : enUS;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({ from: dateFrom, to: dateTo });
  const [selectionStep, setSelectionStep] = useState<"from" | "to">("from");

  useEffect(() => {
    setRange({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (calendarOpen) setSelectionStep("from");
  }, [calendarOpen]);

  const comparison = compareMode !== "none" ? getComparisonRange(dateFrom, dateTo, compareMode) : null;

  const periodLabel = `${format(dateFrom, "dd MMM yyyy", { locale })} – ${format(dateTo, "dd MMM yyyy", { locale })}`;
  const compLabel = comparison
    ? `${format(comparison.compareFrom, "dd MMM yyyy", { locale })} – ${format(comparison.compareTo, "dd MMM yyyy", { locale })}`
    : null;

  function applyQuick(type: "today" | "7d" | "30d" | "month") {
    const now = new Date();
    let from: Date;
    const to: Date = endOfDay(now);
    if (type === "today") {
      from = startOfDay(now);
    } else if (type === "7d") {
      from = startOfDay(subDays(now, 6));
    } else if (type === "30d") {
      from = startOfDay(subDays(now, 29));
    } else {
      from = startOfMonth(now);
    }
    onDateFromChange(from);
    onDateToChange(to);
    setRange({ from, to });
    setSelectionStep("from");
  }

  function handleDayClick(day: Date) {
    const clicked = startOfDay(day);

    if (selectionStep === "from") {
      const nextRange = { from: clicked, to: undefined };
      setRange(nextRange);
      onDateFromChange(clicked);
      onDateToChange(endOfDay(clicked));
      setSelectionStep("to");
      return;
    }

    const from = range?.from ? startOfDay(range.from) : clicked;
    const nextFrom = isBefore(clicked, from) ? clicked : from;
    const nextTo = isBefore(clicked, from) ? from : clicked;
    setRange({ from: nextFrom, to: nextTo });
    onDateFromChange(nextFrom);
    onDateToChange(endOfDay(nextTo));
    setSelectionStep("from");
    setCalendarOpen(false);
  }

  const isQuickActive = (type: "today" | "7d" | "30d" | "month") => {
    const now = new Date();
    if (type === "today") {
      return (
        format(dateFrom, "yyyy-MM-dd") === format(startOfDay(now), "yyyy-MM-dd") &&
        format(dateTo, "yyyy-MM-dd") === format(endOfDay(now), "yyyy-MM-dd")
      );
    }
    if (type === "7d") {
      return format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 6)), "yyyy-MM-dd");
    }
    if (type === "30d") {
      return format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 29)), "yyyy-MM-dd");
    }
    if (type === "month") {
      return format(dateFrom, "yyyy-MM-dd") === format(startOfMonth(now), "yyyy-MM-dd");
    }
    return false;
  };

  return (
    <div className="rounded-2xl border bg-card p-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      {/* Left — context */}
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t("appointmentsOperation")}
          </span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight truncate">{periodLabel}</h2>
        {compLabel && (
          <p className="text-sm text-muted-foreground">
            {t("comparedTo")}{" "}
            <span>{compLabel}</span>
          </p>
        )}
        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
          <span className="font-semibold text-foreground">{totalAppointments}</span>
          <span>{t("attendances").toLowerCase()}</span>
          <span>·</span>
          <span>{t("activeProfessionalsCount", { count: activeProfessionals })}</span>
        </div>
      </div>

      {/* Right — controls */}
      <div className="flex flex-col gap-3 shrink-0">
        {/* Quick pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["today", "7d", "30d", "month"] as const).map((q) => {
            const labels = {
              today: t("quickToday"),
              "7d": t("quick7d"),
              "30d": t("quick30d"),
              month: t("quickThisMonth"),
            };
            return (
              <Button
                key={q}
                size="sm"
                variant={isQuickActive(q) ? "default" : "outline"}
                className="h-7 px-3 text-xs rounded-full"
                onClick={() => applyQuick(q)}
              >
                {labels[q]}
              </Button>
            );
          })}
        </div>

        {/* Date range picker */}
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-2 justify-start">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate max-w-[220px]">{periodLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={range}
              onDayClick={handleDayClick}
              numberOfMonths={2}
              locale={locale}
            />
          </PopoverContent>
        </Popover>

        {/* Compare mode */}
        <Select value={compareMode} onValueChange={(v) => onCompareModeChange(v as CompareMode)}>
          <SelectTrigger className="h-8 text-xs w-full">
            <SelectValue placeholder={t("compareModeLabel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="prev-month">{t("compareModePrevMonth")}</SelectItem>
            <SelectItem value="prev-year">{t("compareModePrevYear")}</SelectItem>
            <SelectItem value="prev-period">{t("compareModePrevPeriod")}</SelectItem>
            <SelectItem value="none">{t("compareModeNone")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
